import { type Ref, ref } from 'vue'
import { http } from '~/shared/api/client'
import { APP_VERSION } from '../constants'

/**
 * Version skew (frontend phase-14 §2.3).
 *
 * The SPA and the API deploy independently, so a tab left open across a deploy is talking to an
 * API that moved. Every V2 response carries `X-Api-Version`; this watches it.
 *
 * Three rules, and the first is the important one:
 *
 * 1. **Never blocking.** A prompt, dismissible, and the app keeps working. Forcing a reload
 *    mid-form loses the form, and the user was in the middle of something.
 * 2. **Only on a real mismatch.** A patch difference is not a skew — the contract version
 *    changes when the *shape* of a response changes, so only the major and minor parts are
 *    compared. Prompting on every deploy trains people to ignore the prompt.
 * 3. **A `410` or an unknown endpoint raises the same prompt** rather than a crash: an endpoint
 *    that has gone means this build is talking to an API that no longer has its routes.
 *
 * Installed once, from `main.ts`, as an axios interceptor — not per component. It reads
 * responses that are being made anyway, so it costs no request of its own.
 */

/** True once the API has told us it is a version this build was not built for. */
const stale = ref(false)
/** What the API says it is, for the prompt's detail line. */
const apiVersion = ref<string | null>(null)
/** Dismissed for this tab. A new mismatch after a dismissal prompts again. */
let dismissedFor: string | null = null

function majorMinor(version: string): string {
  const parts = String(version).split('.')
  return `${parts[0] ?? '0'}.${parts[1] ?? '0'}`
}

/** Exported for the test: the comparison, with nothing else attached. */
export function isSkewed(clientVersion: string, serverVersion: string | null): boolean {
  if (!serverVersion) return false
  return majorMinor(clientVersion) !== majorMinor(serverVersion)
}

function note(serverVersion: string | null) {
  if (serverVersion) apiVersion.value = serverVersion
  if (!isSkewed(APP_VERSION, serverVersion)) return
  if (dismissedFor === serverVersion) return
  stale.value = true
}

/**
 * Watch every response for the version header, and treat a `410` or an unknown endpoint as the
 * same signal.
 *
 * Returns an uninstall function, which the tests use; the app installs it once and never
 * removes it.
 */
export function installVersionSkewDetection(): () => void {
  const onFulfilled = http.interceptors.response.use(
    (response) => {
      note((response.headers?.['x-api-version'] as string) ?? null)
      return response
    },
    (error) => {
      const status = error?.response?.status
      const header = error?.response?.headers?.['x-api-version'] as string | undefined
      if (header) note(header)
      // 410 Gone, or a 404 on a path the build believes in: this build is older than the API.
      // Not treated as an error state of its own — the caller still gets its rejection and
      // renders its own error; the prompt appears alongside it.
      if (status === 410) stale.value = true
      return Promise.reject(error)
    },
  )
  return () => http.interceptors.response.eject(onFulfilled)
}

export function useVersionSkew(): {
  stale: Ref<boolean>
  apiVersion: Ref<string | null>
  clientVersion: string
  dismiss: () => void
  refresh: () => void
} {
  return {
    stale,
    apiVersion,
    clientVersion: APP_VERSION,
    dismiss() {
      dismissedFor = apiVersion.value
      stale.value = false
    },
    refresh() {
      // A hard reload rather than a router navigation: the point is to fetch the new bundle,
      // and the router would happily keep running the old one.
      if (typeof window !== 'undefined') window.location.reload()
    },
  }
}

/** Test seam: forget everything this module has observed. */
export function resetVersionSkew(): void {
  stale.value = false
  apiVersion.value = null
  dismissedFor = null
}
